<script setup lang="ts">
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type {
  BookingStop,
  StopHint,
  StopNumber,
} from '@/models/advancedJobBooking';

import { computed, onMounted, ref } from 'vue';

import JobBookingStopCard from '@/components/cards/JobBookingStopCard.vue';
import StopTimeWindowTimelineDialog from '@/components/dialogs/StopTimeWindowTimelineDialog.vue';
import AdvancedBookingFormSection from '@/components/sections/AdvancedBookingFormSection.vue';
import {
  clampStopsToMinTimes,
  formatStopTime,
  stopAvailableFrom,
  stopIsPriority,
  stopMinTime,
} from '@/helpers/stopsTimeline';
import { createEmptyStop } from '@/models/advancedJobBooking';

export interface Props {
  customerCode?: string;
  addressItems?: openapi.AddressGETOpenapi[];
  addressLoading?: boolean;
  maxStops?: number;
}

const props = withDefaults(defineProps<Props>(), {
  customerCode: '',
  addressItems: () => [],
  addressLoading: false,
  maxStops: 3,
});

const stops = defineModel<BookingStop[]>({
  default: () => [createEmptyStop(1), createEmptyStop(2), createEmptyStop(3)],
});

// Interstate leg: this stop's state differs from the previous stop's state.
function interstateFor(index: number): boolean {
  if (index <= 0) return false;
  const prevState = stops.value[index - 1].state;
  const state = stops.value[index].state;
  return !!prevState && !!state && prevState !== state;
}

// Second leg (stop 2 → stop 3) is a customer-to-customer delivery
function customerToCustomerFor(index: number): boolean {
  if (stops.value.length < 3 || (index !== 1 && index !== 2)) return false;
  const stop2 = stops.value[1];
  const stop3 = stops.value[2];
  return !stop2.warehouse && !stop3.warehouse && !!stop2.name && !!stop3.name;
}

function hintsFor(stop: BookingStop, index: number): StopHint[] {
  const result: StopHint[] = [];
  const availableFrom = stopAvailableFrom(stops.value, index);
  if (availableFrom) {
    // "Available" prefix comes from the fieldset's timeline trigger.
    result.push({
      text: `from ${formatStopTime(availableFrom)} · 30 min after previous stop`,
      type: 'info',
    });
  }
  if (stopIsPriority(stop)) {
    result.push({
      text: 'This time window will incur a Priority Service which will include additional charges',
      type: 'warning',
    });
  } else if (stop.time_mode !== 'any') {
    result.push({
      text: 'Requested Times May Incur Additional Charges',
      type: 'warning',
    });
  }
  return result;
}

// Per-stop view data, recomputed only when stops change
const stopViews = computed(() =>
  stops.value.map((stop, index) => ({
    stop,
    minTime: stopMinTime(stops.value, index),
    interstate: interstateFor(index),
    customerToCustomer: customerToCustomerFor(index),
    hints: hintsFor(stop, index),
  })),
);

// Ensure each stop's start_date is >= the previous stop's start_date.
// Returns unchanged references for stops that need no adjustment.
function clampStopsDates(stopsArr: BookingStop[]): BookingStop[] {
  let prevStartDate: string | null = null;
  return stopsArr.map((stop) => {
    const startDate =
      stop.start_date && prevStartDate && stop.start_date < prevStartDate
        ? prevStartDate
        : (stop.start_date ?? null);
    prevStartDate = startDate;
    const endDate = stop.link_dates
      ? startDate
      : stop.end_date && startDate && stop.end_date < startDate
        ? startDate
        : (stop.end_date ?? null);
    if (startDate === stop.start_date && endDate === stop.end_date) return stop;
    return { ...stop, start_date: startDate, end_date: endDate };
  });
}

// Update one stop, then re-clamp all so later times and dates stay valid.
function updateStop(index: number, value: BookingStop): void {
  const next = [...stops.value];
  next[index] = value;
  stops.value = clampStopsToMinTimes(clampStopsDates(next));
}

// Clamp preset/initial values too, not just user edits.
onMounted(() => {
  const clamped = clampStopsToMinTimes(clampStopsDates(stops.value));
  if (clamped.some((stop, index) => stop !== stops.value[index])) {
    stops.value = clamped;
  }
});

const isTimelineOpen = ref(false);

function openTimeline(): void {
  isTimelineOpen.value = true;
}

// Reorder stops: full stop data moves with the stop. Position 0's time window
// is preserved as the job start time — whatever stop ends up first inherits it.
// Everything from position 1 onward is then re-clamped to maintain the 30-min gap.
function moveStop(fromIndex: number, toIndex: number): void {
  if (toIndex < 0 || toIndex >= stops.value.length) return;

  // Snapshot the time window that was at position 0 before the move.
  const firstStop = stops.value[0];
  const firstTimeSlot = {
    time_mode: firstStop.time_mode,
    am_pm: firstStop.am_pm,
    time_from: firstStop.time_from,
    time_to: firstStop.time_to,
  };

  const reordered = [...stops.value];
  const [moved] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, moved);

  const renumbered = reordered.map((stop, index) => ({
    ...stop,
    number: (index + 1) as StopNumber,
  }));

  // Re-anchor position 0 to the original start time so the job start never drifts.
  renumbered[0] = { ...renumbered[0], ...firstTimeSlot };

  stops.value = clampStopsToMinTimes(clampStopsDates(renumbered));
}

function addStop(): void {
  if (stops.value.length >= props.maxStops) return;
  const lastStop = stops.value[stops.value.length - 1];
  const newStop: BookingStop = {
    ...createEmptyStop((stops.value.length + 1) as StopNumber),
    start_date: lastStop.start_date,
    end_date: lastStop.end_date,
    link_dates: lastStop.link_dates,
  };
  stops.value = clampStopsToMinTimes(
    clampStopsDates([...stops.value, newStop]),
  );
}

function removeStop(): void {
  if (stops.value.length <= 2) return;
  stops.value = clampStopsToMinTimes(clampStopsDates(stops.value.slice(0, -1)));
}
</script>

<template>
  <AdvancedBookingFormSection
    label="Stops"
    :class="$style.jobBookingStopsSection"
    data-testid="jobBookingStopsSection"
  >
    <div :class="$style.stopsGrid" :style="{ '--stop-count': props.maxStops }">
      <JobBookingStopCard
        v-for="(view, index) in stopViews"
        :key="view.stop.number"
        :model-value="view.stop"
        :index="index"
        :total-stops="stopViews.length"
        :customer-code="props.customerCode"
        :address-items="props.addressItems"
        :address-loading="props.addressLoading"
        :min-time="view.minTime"
        :interstate="view.interstate"
        :customer-to-customer="view.customerToCustomer"
        :hints="view.hints"
        :deletable="stopViews.length > 2 && index === stopViews.length - 1"
        @update:model-value="updateStop(index, $event)"
        @move-left="moveStop(index, index - 1)"
        @move-right="moveStop(index, index + 1)"
        @delete="removeStop"
        @open-timeline="openTimeline"
      />

      <button
        v-if="stops.length < props.maxStops"
        type="button"
        :class="$style.addStopCard"
        data-testid="addStopButton"
        @click="addStop"
      >
        <v-icon icon="mdi-plus" size="large" />
        <span>ADD STOP</span>
      </button>
    </div>

    <StopTimeWindowTimelineDialog v-model="isTimelineOpen" :stops="stops" />
  </AdvancedBookingFormSection>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/brand-primary' as bp;

.stopsGrid {
  display: grid;
  grid-template-columns: repeat(var(--stop-count), 1fr);
  gap: 24px;
}

.addStopCard {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 120px;
  border: 2px dashed colors.$color-gray-300;
  border-radius: 12px;
  background: none;
  color: colors.$color-text-secondary;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: bp.$color-brand-400;
    color: bp.$color-brand-700;
  }
}
</style>
